import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import styles from './dashboardGrid.module.css'
import { useMemo, useState } from 'react'
import { Layout, Responsive, WidthProvider } from 'react-grid-layout'
import { DashboardWidgetPreference } from '../../../system'
import { DASHBOARD_COLUMNS, DASHBOARD_ROW_HEIGHT, DashboardWidgetDefinition, widgetSizeGrid } from '../../types'

const ResponsiveGridLayout = WidthProvider(Responsive)

// Breakpoints by the width of the dashboard (not of the window). Only the wide layout is configured:
// the narrower ones are derived from it by the library
const WIDE = 'lg'

// Plain class names (not CSS modules) the grid library uses to know where a drag starts and where it doesn't
const DRAG_HANDLE_CLASS = 'dashboard-drag-handle'
const NO_DRAG_CLASS = 'dashboard-no-drag'
const breakpoints = { lg: 1000, md: 600, sm: 0 }
const cols = { lg: DASHBOARD_COLUMNS, md: 6, sm: 1 }

type Props<T> = {
    widgets: DashboardWidgetDefinition<T>[]
    preferences: DashboardWidgetPreference[]
    data: T
    editing?: boolean
    onLayoutChange?: (layout: Layout[]) => void
    onHideWidget?: (id: string) => void
}

// Visible cards placed where the user left them. While editing they can be moved, resized and hidden
export const DashboardGrid = <T,>(props: Props<T>) => {
    const { widgets, preferences, data, editing = false, onLayoutChange, onHideWidget } = props

    const [breakpoint, setBreakpoint] = useState(WIDE)
    const canEdit = editing && breakpoint === WIDE

    const visibleWidgets = useMemo(() => preferences
        .filter(preference => preference.visible)
        .map(preference => ({ preference, widget: widgets.find(widget => widget.id === preference.id)! }))
        .filter(({ widget }) => !!widget)
    , [preferences, widgets])

    const layout = useMemo((): Layout[] => visibleWidgets.map(({ preference, widget }) => {
        const size = widgetSizeGrid[widget.defaultSize]
        const hasPosition = Number.isFinite(preference.x) && Number.isFinite(preference.y)
        return {
            i: preference.id,
            // Without a position the card goes to the end of the grid
            x: hasPosition ? preference.x! : 0,
            y: hasPosition ? preference.y! : Infinity,
            w: preference.w || size.w,
            h: preference.h || size.h,
            minW: size.minW,
            minH: size.minH
        }
    }), [visibleWidgets])

    const handleLayoutChange = (currentLayout: Layout[]) => {
        if (canEdit) {
            onLayoutChange?.(currentLayout)
        }
    }

    if (visibleWidgets.length === 0) {
        return <p className={styles.empty}>No hay tarjetas visibles. Usá "Personalizar" para elegir qué ver en el inicio.</p>
    }

    return (
        <>
            {editing && !canEdit && (
                <p className={styles.narrowHint}>Para mover y cambiar el tamaño de las tarjetas usá una pantalla más ancha.</p>
            )}
            <ResponsiveGridLayout
                className={`${styles.grid} ${canEdit ? styles.editing : ''}`}
                layouts={{ [WIDE]: layout }}
                breakpoints={breakpoints}
                cols={cols}
                rowHeight={DASHBOARD_ROW_HEIGHT}
                margin={[12, 12]}
                containerPadding={[0, 0]}
                isDraggable={canEdit}
                isResizable={canEdit}
                draggableHandle={`.${DRAG_HANDLE_CLASS}`}
                draggableCancel={`.${NO_DRAG_CLASS}`}
                onBreakpointChange={setBreakpoint}
                onLayoutChange={handleLayoutChange}
            >
                {visibleWidgets.map(({ preference, widget }) => (
                    <div key={preference.id} className={styles.item}>
                        {widget.render(data)}
                        {canEdit && (
                            <div className={styles.editOverlay}>
                                <div className={`${styles.dragHandle} ${DRAG_HANDLE_CLASS}`} title={`Mover "${widget.title}"`}>
                                    <span className={styles.editControls}>
                                        <i className="bi bi-arrows-move" />
                                        <button
                                        type="button"
                                        className={`${styles.hideButton} ${NO_DRAG_CLASS}`}
                                        title="Ocultar tarjeta"
                                        onClick={() => onHideWidget?.(preference.id)}
                                    >
                                            <i className="bi bi-eye-slash" />
                                        </button>
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </ResponsiveGridLayout>
        </>
    )
}
